import React from 'react';
import { useStore } from 'react-redux';

import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store/reduxTypes';
import { completeCoachmark, setCoachmarkActive } from 'src/store/ui/actions';
import {
  selectCoachmarkActive,
  selectCoachmarkCompleted,
} from 'src/store/ui/selectors';
import { CoachmarkTourKey } from 'src/store/ui/types';

export interface CoachmarkTourCoordination {
  // True while the tour's interactive step is engaged (drives coordinated UI).
  active: boolean;
  // True once the tour's goal action has been taken.
  completed: boolean;
  setActive: (next: boolean) => void;
  complete: () => void;
  // Reads the live `active` flag straight from the store, for imperative callers
  // (e.g. a timer) that must observe completion the instant it dispatches rather
  // than on the next React render.
  getActive: () => boolean;
}

export const useStudioTaskStatuses = (
  key: CoachmarkTourKey,
): CoachmarkTourCoordination => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const active = useAppSelector(selectCoachmarkActive(key));
  const completed = useAppSelector(selectCoachmarkCompleted(key));

  const setActive = React.useCallback(
    (next: boolean): void => {
      dispatch(setCoachmarkActive(key, next));
    },
    [dispatch, key],
  );

  const complete = React.useCallback((): void => {
    dispatch(completeCoachmark(key));
  }, [dispatch, key]);

  const getActive = React.useCallback(
    (): boolean => selectCoachmarkActive(key)(store.getState()),
    [store, key],
  );

  return { active, completed, setActive, complete, getActive };
};
